import { objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import { StyledButton, StyledButtonCountdown, StyledLoadingImg } from './Button.styles.js';
import LoadingImg from '../assets/img/loading.gif.js';
import Countdown from '../Countdown/Countdown.js';
const _excluded = ["children", "onClick", "classes", "startIcon", "endIcon", "divide", "disabled", "isLoading", "loadingText", "resendText", "resendTimestamp", "timeFormat"];
const Button = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
      children,
      onClick,
      classes,
      startIcon,
      endIcon,
      divide,
      disabled,
      isLoading,
      loadingText,
      resendText,
      resendTimestamp,
      timeFormat
    } = _ref,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  const endIconNode = isLoading ? /*#__PURE__*/React__default.createElement(StyledLoadingImg, {
    src: LoadingImg,
    alt: "loading"
  }) : endIcon;
  const [isExpired, setIsExpired] = useState(!resendTimestamp);
  const childrenNode = () => {
    if (!isExpired && resendText) {
      return resendText;
    }
    if (isLoading) {
      return loadingText;
    }
    return children;
  };
  const isDisabled = disabled || isLoading || !isExpired;
  const handleClick = e => {
    if (isLoading) {
      return;
    }
    onClick(e);
  };
  return /*#__PURE__*/React__default.createElement(StyledButton, _extends({
    ref: ref,
    classes: classes,
    startIcon: startIcon,
    endIcon: endIconNode,
    divide: divide,
    disabled: isDisabled,
    onClick: handleClick
  }, otherProps), childrenNode(), resendTimestamp && /*#__PURE__*/React__default.createElement(StyledButtonCountdown, null, /*#__PURE__*/React__default.createElement(Countdown, {
    expireTime: resendTimestamp,
    onCounting: () => setIsExpired(false),
    onExpired: () => setIsExpired(true)
  })));
});
Button.propTypes = {
  /**
   * The content of the button.
   */
  children: PropTypes.node,
  /**
   * The loadingText of the button.
   */
  loadingText: PropTypes.node,
  resendText: PropTypes.node,
  /**
   * Override or extend the styles applied to the component. See CSS API below for more details.
   */
  classes: PropTypes.objectOf(PropTypes.shape({})),
  /**
   * The color of the component. It supports those theme colors that make sense for this component.
   */
  color: PropTypes.oneOf(['inherit', 'primary', 'secondary', 'success', 'error', 'info', 'warning', 'setting']),
  /**
   * The variant to use.
   */
  variant: PropTypes.oneOf(['contained', 'outlined', 'text', 'shadow']),
  /**
   * The size of the button. small is equivalent to the dense button styling.
   */
  size: PropTypes.oneOf(['extraLarge', 'large', 'medium', 'small', 'extraSmall', 'extraSmallSmall']),
  /**
  /**
   * If true, the button will be disabled.
   */
  disabled: PropTypes.bool,
  /**
   * If true, the keyboard focus ripple will be disabled.
   */
  disableFocusRipple: PropTypes.bool,
  /**
   * If true, the ripple effect will be disabled.
   * ⚠️ Without a ripple there is no styling for :focus-visible by default.
   * Be sure to highlight the element by applying separate styles with the focusVisibleClassName.
   */
  disableRipple: PropTypes.bool,
  /**
   * If true, no elevation is used.
   */
  disableElevation: PropTypes.bool,
  /**
   * onClickEvent
   */
  onClick: PropTypes.func,
  /**
   * Element placed before the children.
   */
  startIcon: PropTypes.node,
  /**
   * Element placed after the children.
   */
  endIcon: PropTypes.node,
  /**
   * Loading status
   */
  isLoading: PropTypes.bool,
  /**
   * Is divide space
   */
  divide: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  resendTimestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  timeFormat: PropTypes.string
};
Button.defaultProps = {
  children: null,
  loadingText: 'Loading...',
  resendText: 'Resend code',
  classes: null,
  color: 'secondary',
  variant: 'contained',
  size: 'large',
  disabled: false,
  disableFocusRipple: true,
  disableRipple: true,
  disableElevation: true,
  onClick: () => {},
  startIcon: null,
  endIcon: null,
  isLoading: false,
  divide: false,
  resendTimestamp: null,
  timeFormat: 'mm:ss'
};
var Button$1 = Button;
export { Button$1 as default };